import React, { useEffect, useState } from "react";
import { Loader2, Save, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@cms/ui/components/dialog";
import { Button } from "@cms/ui/components/button";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { MediaThumb } from "./media-library-views";
import {
  fileTypeLabel,
  formatFileSize,
  mediaDisplayName,
  type MediaItem,
} from "../lib/media";

/**
 * Detail editor for one library item: title, caption and alt text, plus the
 * file facts and a delete entry point (delete confirmation runs on the page,
 * where usage is checked).
 */
export function MediaEditDialog({ item, assetsBaseUrl, saving, onSave, onDelete, onClose }: {
  item: MediaItem | null;
  assetsBaseUrl: string | null;
  saving: boolean;
  onSave: (item: MediaItem, fields: { title: string; caption: string; alt: string }) => void;
  onDelete: (item: MediaItem) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [alt, setAlt] = useState("");

  useEffect(() => {
    if (!item) return;
    setTitle(item.title);
    setCaption(item.caption);
    setAlt(item.alt);
  }, [item]);

  if (!item) return null;

  const dirty =
    title.trim() !== item.title || caption.trim() !== item.caption || alt.trim() !== item.alt;

  return (
    <Dialog open onOpenChange={(open: boolean) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="truncate">{mediaDisplayName(item)}</DialogTitle>
          <DialogDescription>
            {fileTypeLabel(item)} · {formatFileSize(item.size)}
            {item.width && item.height ? ` · ${item.width}×${item.height}px` : ""}
          </DialogDescription>
        </DialogHeader>

        <MediaThumb item={item} assetsBaseUrl={assetsBaseUrl} className="max-h-56 w-full rounded-md bg-muted" />

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="media-title">Title</Label>
            <Input
              id="media-title"
              value={title}
              maxLength={150}
              placeholder={item.fileName || "Give this file a name"}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="media-caption">Caption</Label>
            <Input
              id="media-caption"
              value={caption}
              maxLength={500}
              placeholder="Shown under the photo where captions are displayed"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCaption(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="media-alt">Alt text</Label>
            <Input
              id="media-alt"
              value={alt}
              maxLength={300}
              placeholder="Describe the image for screen readers and search engines"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAlt(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="ghost"
            className="text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(item)}
            disabled={saving}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              disabled={!dirty || saving}
              onClick={() => onSave(item, { title: title.trim(), caption: caption.trim(), alt: alt.trim() })}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
